import {
    createDisposer,
    getNetworkState,
    type LogTarget,
    logWarn,
    type Maybe,
    type MaybePromise,
    memoize,
    onLine,
    type Unsubscribe,
} from '@amazon/vinyl-util'
import { createErrorHandler } from '@/errorHandler'
import { data, type ObservableValue } from '@amazon/vinyl-observable'

/**
 * Requests data, and on rejection, retries when next online.
 *
 * @param target
 * @param fetch
 * @param callback
 */
export function retryWhenOnLine<T>(
    target: LogTarget,
    fetch: () => MaybePromise<T>,
    callback: (data: T) => any
): void {
    const network = getNetworkState()
    const errorHandler = createErrorHandler(target)
    Promise.resolve(fetch())
        .then(callback)
        .catch((error) => {
            if (network.onLine) {
                errorHandler(error)
            } else {
                logWarn(target, error)
                network.on(
                    'online',
                    () => {
                        retryWhenOnLine(target, fetch, callback)
                    },
                    { once: true }
                )
            }
        })
}

/**
 * @param props Callbacks for when the onLine status has changed. Will be invoked once immediately.
 */
export function onOnlineChange(props: {
    readonly onOffline?: Maybe<() => void>
    readonly onOnline?: Maybe<() => void>
}): Unsubscribe {
    const { add, dispose } = createDisposer()
    const network = getNetworkState()
    if (onLine()) props.onOnline?.()
    else props.onOffline?.()
    if (props.onOnline) add(network.on('online', props.onOnline))
    if (props.onOffline) add(network.on('offline', props.onOffline))
    return dispose
}

export const isOnline = memoize((): ObservableValue<boolean> => {
    const network = getNetworkState()
    const v = data(onLine())
    const refresh = () => (v.value = onLine())
    network.on('online', refresh)
    network.on('offline', refresh)
    return v
})

export const isNotOnline = () => isOnline().map((v) => !v)
